import { TeamInfo } from "@/api/teamCompare";
import { getPointsBadge, getTeamLogo } from "@/lib/utils";

interface TeamStatsPanelProps {
    chosenInfo: TeamInfo;
    opponentInfo: TeamInfo;
}

const STATISTICS = [
    { key: "goals", label: "Goals" },
    { key: "passes", label: "Passes" },
    { key: "shots", label: "Shots" },
    { key: "accurateShots", label: "Accurate Shots" },
    { key: "shotAccuracyPercent", label: "Shot Accuracy %" },
    { key: "corsiFor", label: "Corsi For %" },
    { key: "fenwickFor", label: "Fenwick For %" },
] as const;

const getStatValue = (info: TeamInfo, key: string): string | number => {
    if (key === "shotAccuracyPercent") {
        if (info.shotAccuracyPercent !== undefined) return info.shotAccuracyPercent.toFixed(1) + "%";
        if (info.totalShots && info.totalShots > 0)
            return ((info.accurateShots / info.totalShots) * 100).toFixed(1) + "%";
        if (info.shots > 0) return ((info.accurateShots / info.shots) * 100).toFixed(1) + "%";
        return "0.0%";
    }
    if (key === "corsiFor") return (info.crosiFor?.toFixed(1) ?? "0.0") + "%";
    if (key === "fenwickFor") return (info.fanwickFor?.toFixed(1) ?? "0.0") + "%";
    const val = (info as Record<string, unknown>)[key];
    return typeof val === "number" ? val : 0;
};

const getBetter = (a: string | number, b: string | number): "first" | "second" | "equal" => {
    const va = parseFloat(a.toString().replace("%", ""));
    const vb = parseFloat(b.toString().replace("%", ""));
    if (va > vb) return "first";
    if (vb > va) return "second";
    return "equal";
};

export const TeamStatsPanel = ({ chosenInfo, opponentInfo }: TeamStatsPanelProps) => {
    const chosenKpiBadge = getPointsBadge(chosenInfo.avgKpi);
    const opponentKpiBadge = getPointsBadge(opponentInfo.avgKpi);
    const winSumChosen = chosenInfo.sumKpi > opponentInfo.sumKpi;
    const winSumOpponent = opponentInfo.sumKpi > chosenInfo.sumKpi;
    const chosenLogo = getTeamLogo(chosenInfo.teamName);
    const opponentLogo = getTeamLogo(opponentInfo.teamName);

    return (
        <div className="flex flex-col h-full">
            <h3 className="text-lg font-headline font-bold mb-3 section-title">Team Stats</h3>
            <div className="rounded-xl overflow-x-auto data-table-wrapper">
                <table className="w-full">
                    <thead>
                        <tr className="data-table-header">
                            <th className="px-3 sm:px-6 py-3 font-headline font-black text-[10px] uppercase tracking-widest text-left data-table-label-cell">
                                Metric
                            </th>
                            <th className="px-3 sm:px-6 py-3 font-headline font-black text-[10px] uppercase tracking-widest text-center">
                                <div className="flex items-center justify-center gap-1.5">
                                    <span className="hidden sm:block">{chosenInfo.teamName}</span>
                                    {chosenLogo && (
                                        <img
                                            src={chosenLogo}
                                            alt={chosenInfo.teamName}
                                            className="w-4 h-4 object-contain"
                                        />
                                    )}
                                </div>
                            </th>
                            <th className="px-3 sm:px-6 py-3 font-headline font-black text-[10px] uppercase tracking-widest text-center">
                                <div className="flex items-center justify-center gap-1.5">
                                    <span className="hidden sm:block">{opponentInfo.teamName}</span>
                                    {opponentLogo && (
                                        <img
                                            src={opponentLogo}
                                            alt={opponentInfo.teamName}
                                            className="w-4 h-4 object-contain"
                                        />
                                    )}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {/*<tr>
                            <td
                                colSpan={3}
                                className="px-6 py-2.5 text-[10px] font-headline font-black uppercase tracking-widest text-on-surface"
                                style={{
                                    background: "var(--color-surface-container-highest)",
                                    borderBottom: "1px solid var(--color-outline-variant)",
                                }}
                            >
                                KPI
                            </td>
                        </tr> */}
                        <tr className="data-table-row-striped">
                            <td className="px-3 sm:px-6 py-3.5 text-sm font-medium text-on-surface-variant whitespace-nowrap data-table-label-cell">
                                Total KPI
                            </td>
                            <td
                                className="px-3 sm:px-6 py-3.5 text-sm text-center"
                                style={{ fontWeight: winSumChosen ? 700 : undefined }}
                            >
                                {chosenInfo.sumKpi.toFixed(1)}
                            </td>
                            <td
                                className="px-6 py-3.5 text-sm text-center"
                                style={{ fontWeight: winSumOpponent ? 700 : undefined }}
                            >
                                {opponentInfo.sumKpi.toFixed(1)}
                            </td>
                        </tr>
                        <tr className="data-table-row-striped">
                            <td className="px-3 sm:px-6 py-3.5 text-sm font-medium text-on-surface-variant whitespace-nowrap data-table-label-cell">
                                Avg KPI
                            </td>
                            <td className="px-3 sm:px-6 py-3.5 text-center">
                                <div
                                    className={`inline-flex items-center px-2.5 py-1 ${chosenKpiBadge.bg} ${chosenKpiBadge.text} rounded font-bold text-sm border ${chosenKpiBadge.border}`}
                                >
                                    {chosenInfo.avgKpi.toFixed(1)}
                                </div>
                            </td>
                            <td className="px-6 py-3.5 text-center">
                                <div
                                    className={`inline-flex items-center px-2.5 py-1 ${opponentKpiBadge.bg} ${opponentKpiBadge.text} rounded font-bold text-sm border ${opponentKpiBadge.border}`}
                                >
                                    {opponentInfo.avgKpi.toFixed(1)}
                                </div>
                            </td>
                        </tr>
                        {/* <tr>
                            <td
                                colSpan={3}
                                className="px-6 py-2.5 text-[10px] font-headline font-black uppercase tracking-widest text-on-surface"
                                style={{
                                    background: "var(--color-surface-container-highest)",
                                    borderTop: "2px solid var(--color-outline-variant)",
                                    borderBottom: "1px solid var(--color-outline-variant)",
                                }}
                            >
                                Statistics
                            </td>
                        </tr> */}
                        {STATISTICS.map((stat) => {
                            const chosenVal = getStatValue(chosenInfo, stat.key);
                            const opponentVal = getStatValue(opponentInfo, stat.key);
                            const better = getBetter(chosenVal, opponentVal);
                            return (
                                <tr key={stat.key} className="data-table-row-striped">
                                    <td className="px-3 sm:px-6 py-3.5 text-sm font-medium text-on-surface-variant whitespace-nowrap data-table-label-cell">
                                        {stat.label}
                                    </td>
                                    <td
                                        className="px-3 sm:px-6 py-3.5 text-sm text-center text-on-surface"
                                        style={{ fontWeight: better === "first" ? 700 : undefined }}
                                    >
                                        {chosenVal}
                                    </td>
                                    <td
                                        className="px-3 sm:px-6 py-3.5 text-sm text-center text-on-surface"
                                        style={{ fontWeight: better === "second" ? 700 : undefined }}
                                    >
                                        {opponentVal}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
